import axios from 'axios';
import { apiUrl } from '../config/api';
import { formatDateDMY } from './dateUtils';
import { toCurrency } from './adminMetrics';

// Cache company configuration to avoid repeated fetches while browsing
let __companyConfigCache = null;
let __companyConfigFetchedAt = 0;
let __companyConfigCacheMode = null; // 'public' | 'private'

export const clearCompanyConfigCache = () => {
  __companyConfigCache = null;
  __companyConfigFetchedAt = 0;
  __companyConfigCacheMode = null;
};

export const getCompanyConfigCached = async (force = false) => {
  try {
    const token = localStorage.getItem('token');
    const desiredMode = token ? 'private' : 'public';
    const cacheFresh = __companyConfigCache && (Date.now() - __companyConfigFetchedAt) < 5 * 60 * 1000;

    // Important: Login page fetches the PUBLIC config before auth.
    // After login we must refetch the PRIVATE config; otherwise setup checks can incorrectly fail.
    if (!force && cacheFresh && __companyConfigCacheMode === desiredMode) {
      return __companyConfigCache;
    }

    // If not authenticated yet, only request non-sensitive public config.
    const url = token ? '/api/company-config' : '/api/company-config/public';
    const res = await axios.get(apiUrl(url), token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
    __companyConfigCache = res.data || null;
    __companyConfigFetchedAt = Date.now();
    __companyConfigCacheMode = desiredMode;
    return __companyConfigCache;
  } catch (e) {
    return null;
  }
};

export const overdueLabel = (dueDate) => {
  if (!dueDate) return '';
  const due = new Date(dueDate);
  const now = new Date();
  if (isNaN(due.getTime()) || now <= due) return '';
  let months = (now.getFullYear() - due.getFullYear()) * 12 + (now.getMonth() - due.getMonth());
  if (now.getDate() < due.getDate()) months -= 1; // full calendar months only
  if (months <= 0) {
    const days = Math.floor((now.getTime() - due.getTime()) / 86400000);
    if (days > 0) return `Overdue by ${days} day${days > 1 ? 's' : ''}`;
    return '';
  }
  if (months < 12) return `Overdue by ${months} month${months > 1 ? 's' : ''}`;
  const years = Math.floor(months / 12);
  return `Overdue by ${years} year${years > 1 ? 's' : ''}`;
};

export const extractClientInfo = (loan) => {
  if (!loan) return { name: 'Client', email: '', phone: '', address: '' };
  const directName = loan.clientName || loan.client_name || `${loan.first_name || ''} ${loan.last_name || ''}`.trim();
  const directEmail = loan.client_email || loan.email || '';
  const directPhone = loan.client_phone || loan.phone_number || loan.mobile || '';
  const directAddress = loan.client_address || loan.postal_address || loan.residential_address || '';
  if (directName && directEmail && directPhone && directAddress) {
    return { name: directName, email: directEmail, phone: directPhone, address: directAddress };
  }
  const client = loan.client || loan.client_profile || loan.client_personal_info || loan.client_personal || {};
  const personalInfo = client.personal_info || client.client_personal_info || {};
  const contactInfo = client.contact_info || client.contact || {};
  const name = directName || client.full_name || client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || `${personalInfo.first_name || ''} ${personalInfo.last_name || ''}`.trim() || 'Client';
  const email = directEmail || client.email || personalInfo.email || contactInfo.email || '';
  const phone = directPhone || client.phone_number || client.mobile || personalInfo.phone_number || personalInfo.mobile || contactInfo.phone || contactInfo.phone_number || '';
  const address = directAddress || client.postal_address || client.residential_address || client.address || personalInfo.postal_address || personalInfo.residential_address || personalInfo.address || contactInfo.address || '';
  return { name, email, phone, address };
};

export const enrichLoanForInvoice = async (loan) => {
  try {
    let enriched = { ...loan };
    const token = localStorage.getItem('token');
    const resolvedClientId = enriched?.client_id ?? enriched?.clientId ?? enriched?.client?.id ?? enriched?.client?.client_id ?? null;

    // Populate client info for both company-side users and client-side users.
    // Company-side: /api/clients/:id/profile
    // Client-side fallback: /api/clients/my-profile
    try {
      let profile = null;
      const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

      if (token && resolvedClientId) {
        try {
          const byIdRes = await axios.get(apiUrl(`/api/clients/${resolvedClientId}/profile`), authHeaders);
          profile = byIdRes.data || null;
        } catch {}
      }

      if (!profile && token) {
        try {
          const mineRes = await axios.get(apiUrl('/api/clients/my-profile'), authHeaders);
          profile = mineRes.data || null;
        } catch {}
      }

      if (profile) {
        const personalInfo = profile.personal_info || {};
        const contactInfo = profile.contact_info || {};
        enriched.client = profile;
        if (!enriched.clientName && !enriched.client_name) {
          const first = personalInfo.first_name || '';
          const last = personalInfo.last_name || '';
          enriched.clientName = (first && last) ? `${first} ${last}`.trim() : (personalInfo.full_name || '').trim();
        }
        enriched.client_email = personalInfo.email || contactInfo.email || profile.email || '';
        enriched.client_phone = personalInfo.phone_number || personalInfo.mobile || contactInfo.phone_number || profile.phone_number || '';
        enriched.client_address = personalInfo.residential_address || personalInfo.postal_address || contactInfo.address || profile.address || '';
      }
    } catch {}

    // Load additional expenses breakdown for invoice rendering.
    try {
      if (token && loan?.id != null) {
        const expRes = await axios.get(apiUrl(`/api/loans/${loan.id}/additional-expenses`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const expenseRows = Array.isArray(expRes.data?.expenses) ? expRes.data.expenses : [];
        enriched.additional_expenses = expenseRows;
        enriched.additional_expenses_total = Number(expRes.data?.total) || 0;
      }
    } catch {}

    // Load company configuration (name, address, contact, bank) and attach to invoice
    try {
      const cfg = await getCompanyConfigCached();
      if (cfg) {
        enriched.companyConfig = cfg;
        if (cfg.logo_base64) {
          enriched.companyLogoDataUri = cfg.logo_base64;
        }
      }
    } catch {}

    // The generated invoices, for their history. What is still owed
    // (total_due) and the penalty interest stay as the API sent them on the
    // loan: they are not worked out again here.
    try {
      const invRes = await axios.get(apiUrl(`/api/loans/${loan.id}/invoices`), { headers: { Authorization: `Bearer ${token}` } });
      if (Array.isArray(invRes.data) && invRes.data.length) {
        enriched.invoices = invRes.data;
      }
    } catch {}

    return enriched;
  } catch {
    return loan;
  }
};

export const renderInvoiceHtml = (loan, options = {}) => {
  const { darkMode = false } = options;
  // Use embedded data URI to avoid browser same-origin/CORP logo fetch issues.
  const origin = typeof window !== 'undefined' ? (window.location.origin || '') : '';
  const fallbackLogoPng = `${origin}/company-logo.svg`;
  const logoSrc = (loan && loan.companyLogoDataUri) || fallbackLogoPng;
  const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

  const invoiceDate = formatDateDMY(new Date());
  const dueDate = formatDateDMY(loan.due_date);
  const overdue = overdueLabel(loan.due_date);
  const client = extractClientInfo(loan);
  const invoiceNumber = `INV-${loan.id}`;
  const loanRef = loan.loan_code || `Loan #${loan.id}`;

  // What the loan costs, line by line.
  const principal = num(loan.principal_amount);
  const interestRate = num(loan.interest_rate);
  const interest = loan.principal_amount_plus_interest != null
    ? Math.max(0, num(loan.principal_amount_plus_interest) - principal)
    : 0;
  const expenseRows = Array.isArray(loan?.additional_expenses)
    ? loan.additional_expenses.map((e) => ({
        name: e?.expense_name || e?.expense_code || 'Additional expense',
        method: e?.calculation_method || '',
        amount: num(e?.calculated_amount),
      }))
    : [];
  const expensesTotal = expenseRows.length
    ? expenseRows.reduce((sum, e) => sum + e.amount, 0)
    : num(loan.additional_expenses_total);

  // What has been paid, and what is left.
  const repayments = (loan.repayments || [])
    .map((r) => ({ date: r.payment_date || r.date, amount: num(r.amount_paid ?? r.amount) }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  // What was paid and what is still owed are the API's figures (loan.state
  // and total_due); they are not worked out again here.
  const totalPaid = loan?.state && loan.state.total_paid != null
    ? num(loan.state.total_paid)
    : repayments.reduce((sum, r) => sum + r.amount, 0);
  const balance = loan.total_due != null ? num(loan.total_due) : num(loan.outstanding);
  // Penalty interest CHARGED (not the unpaid remainder), so the charges add
  // up: P + I + expenses + penalty charged − payments = balance (total_due).
  // From the API's total_due and state.total_paid; failing those, the
  // penalty table's total; failing that, the loan's penalty_interest.
  const baseCharges = principal + interest + expensesTotal;
  const latestInvoiceForPenalty = Array.isArray(loan?.invoices) && loan.invoices.length ? loan.invoices[0] : null;
  const breakdownTotal = latestInvoiceForPenalty?.penalty_breakdown?.total;
  const penalty = (loan.total_due != null && loan?.state && loan.state.total_paid != null)
    ? Math.max(0, Math.round((balance + totalPaid - baseCharges) * 100) / 100)
    : (breakdownTotal != null ? num(breakdownTotal) : num(loan.penalty_interest));
  const grandTotal = baseCharges + penalty;
  const paidPct = grandTotal > 0 ? Math.min(100, Math.round((totalPaid / grandTotal) * 100)) : 0;

  const isPaid = loan.status === 'Paid Off' || (balance <= 0.004 && totalPaid > 0);
  const status = isPaid
    ? { label: 'Paid', cls: 'paid' }
    : overdue
      ? { label: 'Overdue', cls: 'overdue' }
      : { label: 'Due', cls: 'due' };

  const company = loan && loan.companyConfig ? loan.companyConfig : {};
  const companyName = company.company_name || '';
  const companyAddress = company.address || '';
  const companyEmail = company.email || '';
  const companyPhone = company.phone_number || '';
  const bankName = company.bank_name || '';
  const branchCode = company.branch_code || '';
  const accountNumber = company.account_number || '';
  const accountType = company.account_type || '';
  const hasBank = !!(bankName || branchCode || accountNumber || accountType);

  const latestInvoice = Array.isArray(loan?.invoices) && loan.invoices.length ? loan.invoices[0] : null;
  // Penalty interest month by month, as the API charged it (GN 263/2018):
  // the rule, each month, the total line and the next charge date are the
  // API's (invoices[].penalty_breakdown), shown as they are.
  const penaltyBreakdown = latestInvoice && latestInvoice.penalty_breakdown ? latestInvoice.penalty_breakdown : null;
  const penaltyMonths = penaltyBreakdown && Array.isArray(penaltyBreakdown.months) ? penaltyBreakdown.months : [];
  const pct = (rate) => `${Number((num(rate) * 100).toFixed(2))}%`;

  let running = 0;
  const paymentRows = repayments.map((r) => {
    running += r.amount;
    return `<tr><td>${formatDateDMY(r.date)}</td><td class="r">${toCurrency(r.amount)}</td><td class="r muted">${toCurrency(running)}</td></tr>`;
  }).join('');

  const penaltySection = penaltyMonths.length ? `
    <h3 class="sec">Penalty interest</h3>
    <p class="note" style="margin:0 0 6px">${esc(penaltyBreakdown.rule || 'Penalty interest (NAMFISA GN 263/2018: 5% per month on arrears, max 3 months)')}</p>
    <table class="tbl">
      <thead><tr><th>Month</th><th>Charge date</th><th class="r">Arrears</th><th class="r">Rate</th><th class="r">Penalty</th></tr></thead>
      <tbody>
        ${penaltyMonths.map((m) => `<tr>
          <td>Month ${esc(m.penalty_month_no)}</td>
          <td>${formatDateDMY(m.charge_date)}</td>
          <td class="r">${toCurrency(m.arrears_base)}</td>
          <td class="r">${pct(m.rate)}</td>
          <td class="r neg">${toCurrency(m.penalty_amount)}</td>
        </tr>`).join('')}
        ${penaltyBreakdown.summary ? `<tr class="sum"><td colspan="5">${esc(penaltyBreakdown.summary)}</td></tr>` : ''}
      </tbody>
    </table>
    ${penaltyBreakdown.next_charge_date ? `<p class="note">Next charge date: ${formatDateDMY(penaltyBreakdown.next_charge_date)} if still in arrears</p>` : ''}` : '';

  // The first <style> is scoped to .page: the PDF export copies it into the
  // app for a moment, so it must not restyle the app. The page background
  // lives in the second one.
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>Invoice ${invoiceNumber}</title>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <style>
    .page,.page *{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
    .page{max-width:820px;margin:0 auto;background:#fff;color:#1e293b;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:13px;line-height:1.5;box-shadow:0 10px 30px rgba(15,23,42,.18);border-top:6px solid #2e5488}
    .page .pad{padding:28px 36px}
    .page .top{display:flex;justify-content:space-between;align-items:flex-start;gap:24px}
    .page .logo{height:54px;max-width:150px;object-fit:contain}
    .page .co-name{font-size:15px;font-weight:700;color:#0f172a;margin-top:10px}
    .page .co-line{font-size:11.5px;color:#64748b}
    .page .doc-title{font-size:30px;font-weight:800;letter-spacing:.14em;color:#2e5488;text-align:right;line-height:1}
    .page .meta{margin-top:12px;border-collapse:collapse;margin-left:auto}
    .page .meta td{padding:2px 0 2px 16px;font-size:12px;text-align:right}
    .page .meta td:first-child{color:#64748b}
    .page .meta td:last-child{font-weight:600;color:#0f172a}
    .page .pill{display:inline-block;flex-shrink:0;padding:4px 14px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase}
    .page .pill.due{background:#dbeafe;color:#1d4ed8}
    .page .pill.overdue{background:#fee2e2;color:#b91c1c}
    .page .pill.paid{background:#dcfce7;color:#15803d}
    .page .parties{display:flex;gap:16px;margin-top:14px}
    .page .party-head{display:flex;justify-content:space-between;align-items:flex-start;gap:8px}
    .page .party{flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px 16px}
    .page .lbl{font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#64748b;margin-bottom:6px}
    .page .party .name{font-size:14px;font-weight:700;color:#0f172a}
    .page .party .line{font-size:12px;color:#475569}
    .page .sec{font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#2e5488;margin:26px 0 8px;padding-bottom:6px;border-bottom:2px solid #2e5488}
    .page .tbl{width:100%;border-collapse:collapse}
    .page .tbl th{font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#64748b;text-align:left;padding:8px 10px;border-bottom:1px solid #cbd5e1}
    .page .tbl td{padding:9px 10px;border-bottom:1px solid #eef2f7;color:#1e293b}
    .page .tbl .r{text-align:right;white-space:nowrap}
    .page .tbl .sub td{color:#64748b;font-size:12px}
    .page .tbl .sub td:first-child{padding-left:24px}
    .page .tbl .sum td{font-weight:700;border-top:1px solid #cbd5e1;border-bottom:none}
    .page .tbl .paid td{color:#15803d;font-weight:600;border-bottom:none}
    .page .tbl .bal td{padding:7px 10px;font-size:13px;font-weight:700;color:#fff;background:#2e5488;border:none}
    .page .tbl .bal.overdue td{background:#b91c1c}
    .page .tbl .bal.paid td{background:#15803d}
    .page .muted{color:#94a3b8!important}
    .page .neg{color:#b91c1c!important;font-weight:600}
    .page .bar{height:6px;background:#e2e8f0;border-radius:999px;overflow:hidden;margin-top:10px}
    .page .bar > span{display:block;height:100%;background:#16a34a}
    .page .bar-cap{font-size:11px;color:#64748b;margin-top:4px}
    .page .empty{padding:12px 10px;color:#64748b;font-size:12px}
    .page .note{font-size:11px;color:#64748b;margin-top:6px}
    .page .bank{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:10px 24px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px 16px}
    .page .bank .v{font-size:13px;font-weight:600;color:#0f172a}
    .page .ref{margin-top:10px;font-size:12px;color:#475569}
    .page .ref b{color:#0f172a}
    .page .foot{background:#f8fafc;border-top:1px solid #e2e8f0;padding:14px 36px;font-size:11.5px;color:#64748b;text-align:center}
    @media (max-width:600px){
      .page .pad{padding:20px 16px}
      .page .top,.page .parties{flex-direction:column}
      .page .doc-title,.page .meta{text-align:left;margin-left:0}
      .page .bank{grid-template-columns:repeat(2,1fr)}
    }
    @media print{.page{box-shadow:none;max-width:100%}}
  </style>
  <style>
    html,body{margin:0;padding:0}
    body{background:${darkMode ? '#0f172a' : '#e2e8f0'};padding:24px 12px}
    @media print{body{background:#fff;padding:0}}
  </style>
</head>
<body>
<div class="page">
  <div class="pad">
    <div class="top">
      <div>
        <img class="logo" src="${logoSrc}" onerror="this.onerror=null;this.src='${fallbackLogoPng}';" alt="${esc(companyName)}"/>
        <div class="co-name">${esc(companyName)}</div>
        ${companyAddress ? `<div class="co-line">${esc(companyAddress)}</div>` : ''}
        ${(companyEmail || companyPhone) ? `<div class="co-line">${esc([companyEmail, companyPhone].filter(Boolean).join(' · '))}</div>` : ''}
      </div>
      <div style="text-align:right">
        <div class="doc-title">INVOICE</div>
        <table class="meta">
          <tr><td>Invoice no.</td><td>${invoiceNumber}</td></tr>
          <tr><td>Loan</td><td>${esc(loanRef)}</td></tr>
          <tr><td>Issued</td><td>${invoiceDate}</td></tr>
          <tr><td>Due</td><td>${dueDate}</td></tr>
        </table>
      </div>
    </div>

    <div class="parties">
      <div class="party">
        <div class="lbl">Bill to</div>
        <div class="name">${esc(client.name)}</div>
        ${client.email ? `<div class="line">${esc(client.email)}</div>` : ''}
        ${client.phone ? `<div class="line">${esc(client.phone)}</div>` : ''}
        ${client.address ? `<div class="line">${esc(client.address)}</div>` : ''}
      </div>
      <div class="party">
        <div class="party-head">
          <div>
            <div class="lbl">Loan</div>
            <div class="name">${esc(loanRef)}</div>
          </div>
          <span class="pill ${status.cls}">${status.label}${overdue && !isPaid ? ` · ${esc(overdue.replace('Overdue by ', ''))}` : ''}</span>
        </div>
        <div class="line">Principal ${toCurrency(principal)} at ${interestRate.toFixed(2)}%</div>
        ${loan.duration_months ? `<div class="line">${esc(loan.duration_months)} month${Number(loan.duration_months) === 1 ? '' : 's'}${loan.monthly_payment ? ` · ${toCurrency(loan.monthly_payment)} a month` : ''}</div>` : ''}
        ${loan.start_date ? `<div class="line">Started ${formatDateDMY(loan.start_date)}</div>` : ''}
      </div>
    </div>

    <h3 class="sec">Charges</h3>
    <table class="tbl">
      <thead><tr><th>Description</th><th class="r">Amount</th></tr></thead>
      <tbody>
        <tr><td>Principal loan amount</td><td class="r">${toCurrency(principal)}</td></tr>
        <tr><td>Interest (${interestRate.toFixed(2)}%)</td><td class="r">${toCurrency(interest)}</td></tr>
        ${expenseRows.length
          ? `<tr><td>Additional expenses</td><td class="r">${toCurrency(expensesTotal)}</td></tr>
             ${expenseRows.map((e) => `<tr class="sub"><td>${esc(e.name)}${e.method ? ` <span class="muted">(${esc(e.method)})</span>` : ''}</td><td class="r">${toCurrency(e.amount)}</td></tr>`).join('')}`
          : (expensesTotal > 0 ? `<tr><td>Additional expenses</td><td class="r">${toCurrency(expensesTotal)}</td></tr>` : '')}
        ${penalty > 0 ? `<tr><td>Penalty interest charged</td><td class="r neg">${toCurrency(penalty)}</td></tr>` : ''}
        <tr class="sum"><td>Total charges</td><td class="r">${toCurrency(grandTotal)}</td></tr>
        ${totalPaid > 0 ? `<tr class="paid"><td>Less payments received</td><td class="r">− ${toCurrency(totalPaid)}</td></tr>` : ''}
        <tr class="bal ${status.cls}"><td>${isPaid ? 'Balance' : 'Balance due'}</td><td class="r">${toCurrency(balance)}</td></tr>
      </tbody>
    </table>
    ${grandTotal > 0 ? `<div class="bar"><span style="width:${paidPct}%"></span></div><div class="bar-cap">${paidPct}% paid</div>` : ''}

    <h3 class="sec">Payments received</h3>
    ${paymentRows
      ? `<table class="tbl"><thead><tr><th>Date</th><th class="r">Amount</th><th class="r">Paid so far</th></tr></thead><tbody>${paymentRows}</tbody></table>`
      : '<div class="empty">No payments recorded yet.</div>'}

    ${penaltySection}

    ${hasBank ? `
    <h3 class="sec">How to pay</h3>
    <div class="bank">
      ${bankName ? `<div><div class="lbl">Bank</div><div class="v">${esc(bankName)}</div></div>` : ''}
      ${accountType ? `<div><div class="lbl">Account type</div><div class="v">${esc(accountType)}</div></div>` : ''}
      ${accountNumber ? `<div><div class="lbl">Account number</div><div class="v">${esc(accountNumber)}</div></div>` : ''}
      ${branchCode ? `<div><div class="lbl">Branch code</div><div class="v">${esc(branchCode)}</div></div>` : ''}
    </div>
    <div class="ref">Use <b>${esc(loanRef)}</b> as your payment reference.</div>` : ''}
  </div>

  <div class="foot">
    Thank you${companyName ? ` for choosing <strong>${esc(companyName)}</strong>` : ''}. For any questions about this invoice, please contact us${companyEmail ? ` at ${esc(companyEmail)}` : ''}${companyPhone ? ` or ${esc(companyPhone)}` : ''}.
  </div>
</div>
</body>
</html>`;
};
